import { PersistenceValidationError } from '../../application/errors/PersistenceError';
import type { WorkoutPersistenceTransactions } from '../../application/ports/WorkoutPersistenceTransactions';
import type { WorkoutSessionId } from '../../domain/shared/ids';
import type { ExerciseLog } from '../../domain/workout/ExerciseLog';
import type { WorkoutSession } from '../../domain/workout/WorkoutSession';
import type { AscendInvictusDatabase } from './AscendInvictusDatabase';
import { ExerciseLogMapper, WorkoutSessionMapper } from './mappers';
import { executePersistence } from './translatePersistenceError';

export class DexieWorkoutPersistenceTransactions implements WorkoutPersistenceTransactions {
  public constructor(private readonly database: AscendInvictusDatabase) {}

  private assertOwnership(session: WorkoutSession, exercises: readonly ExerciseLog[]): void {
    const invalid = exercises.some(
      (exercise) =>
        exercise.workoutSessionId !== session.id || exercise.profileId !== session.profileId,
    );
    if (invalid) throw new PersistenceValidationError();
  }

  public createSessionWithExercisesIfAbsent(
    session: WorkoutSession,
    exercises: readonly ExerciseLog[],
  ): Promise<boolean> {
    this.assertOwnership(session, exercises);
    const sessionRecord = WorkoutSessionMapper.toRecord(session);
    const exerciseRecords = exercises.map((exercise) => ExerciseLogMapper.toRecord(exercise));

    return executePersistence('write', async () =>
      this.database.transaction(
        'rw',
        this.database.workoutSessions,
        this.database.exerciseLogs,
        async () => {
          const existing = await this.database.workoutSessions
            .where('[profileId+logicalDate]')
            .equals([session.profileId, session.logicalDate])
            .first();
          if (existing) return false;
          await this.database.workoutSessions.add(sessionRecord);
          await this.database.exerciseLogs.bulkAdd(exerciseRecords);
          return true;
        },
      ),
    );
  }

  public updateSessionAndExercise(session: WorkoutSession, exercise: ExerciseLog): Promise<void> {
    this.assertOwnership(session, [exercise]);
    const sessionRecord = WorkoutSessionMapper.toRecord(session);
    const exerciseRecord = ExerciseLogMapper.toRecord(exercise);
    return executePersistence('write', async () => {
      await this.database.transaction(
        'rw',
        this.database.workoutSessions,
        this.database.exerciseLogs,
        async () => {
          await this.database.workoutSessions.put(sessionRecord);
          await this.database.exerciseLogs.put(exerciseRecord);
        },
      );
    });
  }

  public updateSessionAndExercises(
    session: WorkoutSession,
    exercises: readonly ExerciseLog[],
  ): Promise<void> {
    this.assertOwnership(session, exercises);
    const sessionRecord = WorkoutSessionMapper.toRecord(session);
    const exerciseRecords = exercises.map((exercise) => ExerciseLogMapper.toRecord(exercise));
    return executePersistence('write', async () => {
      await this.database.transaction(
        'rw',
        this.database.workoutSessions,
        this.database.exerciseLogs,
        async () => {
          await this.database.workoutSessions.put(sessionRecord);
          await this.database.exerciseLogs.bulkPut(exerciseRecords);
        },
      );
    });
  }

  public listExercises(workoutSessionId: WorkoutSessionId): Promise<readonly ExerciseLog[]> {
    return executePersistence('read', async () => {
      const records = await this.database.exerciseLogs
        .where('workoutSessionId')
        .equals(workoutSessionId)
        .sortBy('order');
      return records.map((record) => ExerciseLogMapper.fromRecord(record));
    });
  }
}
